import { useEffect, useCallback, useRef } from "react";
import { useSocket } from "./useSocket";

/**
 * 📋 CHAT LIST SOCKET HOOK
 * Handles real-time events for conversation list sidebar
 * Optimized for conversation list updates without individual chat details
 */
export const useChatListSocket = ({
  onNewConversation = null,
  onConversationUpdated = null,
  onNewMessage = null,
  onConversationAssigned = null,
  onConversationResolved = null,
  onConversationDisabled = null,
} = {}) => {
  const { socket, isConnected } = useSocket();
  const handlersRef = useRef({
    onNewConversation,
    onConversationUpdated,
    onNewMessage,
    onConversationAssigned,
    onConversationResolved,
    onConversationDisabled,
  });

  // Update handlers ref when props change
  useEffect(() => {
    handlersRef.current = {
      onNewConversation,
      onConversationUpdated,
      onNewMessage,
      onConversationAssigned,
      onConversationResolved,
      onConversationDisabled,
    };
  }, [
    onNewConversation,
    onConversationUpdated,
    onNewMessage,
    onConversationAssigned,
    onConversationResolved,
    onConversationDisabled,
  ]);

  /**
   * 📨 Handle incoming events for conversation list
   */
  const handleAppUpdate = useCallback((event) => {
    const {
      type,
      data,
      conversationId: eventConversationId,
      channelId: eventChannelId,
    } = event;

    // Debug logging with enhanced details
    console.log("🔄 [ChatListSocket] Received event:", {
      type,
      conversationId: eventConversationId,
      channelId: eventChannelId,
      message: data.message?.substring(0, 50) + "...",
      senderType: data.sender_type,
      rawData: data,
      hasHandlers: {
        onNewConversation: !!handlersRef.current.onNewConversation,
        onNewMessage: !!handlersRef.current.onNewMessage,
        onConversationUpdated: !!handlersRef.current.onConversationUpdated,
      },
    });

    // Handle different event types for conversation list
    switch (type) {
      case "new-conversation":
        if (handlersRef.current.onNewConversation) {
          handlersRef.current.onNewConversation({
            conversation: data.conversation,
            customer: data.customer,
            channel: data.channel,
            isNew: data.isNew || true,
          });
        }
        break;

      case "new-message":
        // Update last message and unread count in conversation list
        if (handlersRef.current.onNewMessage) {
          console.log(
            "📋 [ChatListSocket] Processing new-message for conversation list"
          );
          handlersRef.current.onNewMessage({
            conversationId: eventConversationId,
            channelId: eventChannelId,
            message: data.message,
            sender_type: data.sender_type,
            sender_name: data.sender_name,
            timestamp: data.timestamp || data.time || new Date().toISOString(),
            date: data.date || new Date().toLocaleDateString("id-ID"),
            media_type: data.media_type,
            is_media: data.is_media || false,
            // 🎯 CRITICAL: Pass unread_count from backend for realtime badge updates
            unread_count: data.unread_count,
            conversationStatus: data.conversationStatus || data.status,
            // 🔥 NEW: Complete customer & channel data for lazy conversation creation
            customerId: data.customerId,
            customerName: data.customerName,
            customerNumber: data.customerNumber,
            channelType: data.channelType,
            status: data.status || "received",
            fullEventData: data, // Pass complete data for flexibility
          });
        } else {
          console.log(
            "⚠️ [ChatListSocket] No onNewMessage handler for new-message"
          );
        }
        break;

      case "ai-response":
        // Treat AI responses as new messages for conversation list updates
        if (handlersRef.current.onNewMessage) {
          handlersRef.current.onNewMessage({
            conversationId: eventConversationId,
            message: data.message,
            sender_type: "ai",
            sender_name: "AI Assistant",
            timestamp: data.timestamp || new Date().toISOString(),
            date: new Date().toLocaleDateString(),
            media_type: "text",
            is_media: false,
          });
        }
        break;

      case "conversation-updated":
        if (handlersRef.current.onConversationUpdated) {
          handlersRef.current.onConversationUpdated({
            conversationId: eventConversationId,
            status: data.status,
            escalation_reason: data.escalation_reason,
            customer_name: data.customer_name,
            last_message: data.last_message,
            updated_at: data.updated_at || new Date().toISOString(),
          });
        }
        break;

      case "conversation-assigned":
        if (handlersRef.current.onConversationAssigned) {
          handlersRef.current.onConversationAssigned({
            conversationId: eventConversationId,
            assignedTo: data.assignedTo,
            status: data.status || "assigned",
            customer_name: data.customer_name,
            aiDisabled: data.aiDisabled,
          });
        }
        break;

      case "conversation-resolved":
        if (handlersRef.current.onConversationResolved) {
          handlersRef.current.onConversationResolved({
            conversationId: eventConversationId,
            resolvedBy: data.resolvedBy,
            status: "active", // 🔥 FIX: Consistent with UI - resolved conversations go back to "active"
            customer_name: data.customer_name,
            backToAI: data.backToAI,
            resolved_at: data.resolved_at || new Date().toISOString(),
          });
        }
        break;

      case "conversation-disabled":
        if (handlersRef.current.onConversationDisabled) {
          handlersRef.current.onConversationDisabled({
            conversationId: eventConversationId,
            disabledBy: data.disabledBy,
            status: "disable",
            customer_name: data.customer_name,
            disabled_at: data.disabled_at || new Date().toISOString(),
          });
        }
        break;

      default:
        // Ignore other event types for conversation list
        break;
    }
  }, []);

  /**
   * 🔄 Subscribe to socket events
   */
  useEffect(() => {
    if (!socket || !isConnected) return;

    socket.on("app-update", handleAppUpdate);

    return () => {
      socket.off("app-update", handleAppUpdate);
    };
  }, [socket, isConnected, handleAppUpdate]);

  /**
   * 📊 Debug information
   */
  const getDebugInfo = useCallback(() => {
    return {
      isConnected,
      socketId: socket?.id,
      hasHandlers: {
        onNewConversation: !!handlersRef.current.onNewConversation,
        onConversationUpdated: !!handlersRef.current.onConversationUpdated,
        onNewMessage: !!handlersRef.current.onNewMessage,
        onConversationAssigned: !!handlersRef.current.onConversationAssigned,
        onConversationResolved: !!handlersRef.current.onConversationResolved,
        onConversationDisabled: !!handlersRef.current.onConversationDisabled,
      },
    };
  }, [isConnected, socket]);

  return {
    // Connection status
    isConnected,

    // Debug
    getDebugInfo,
  };
};
